// Признанные сервисники РС — кто и на что авторизован Регистром. ТОЛЬКО ЧТЕНИЕ.
//
// ГЛАВНЫЙ ВОПРОС ЭКРАНА: «что этот сервисник вправе делать и до какого срока». Ответ — не
// строка таблицы, а карточка: в данных одна компания занимает до пятидесяти строк, потому что
// строка реестра — это «компания × область работ × поручитель × срок». Поэтому список
// сгруппирован по компании (281 компания на 2 652 строки, на 28.07.2026), а области
// с поручителями и сроками свёрнуты в карточке. Показывать их списком значило бы выдать
// одну компанию за пятьдесят разных сервисников.
//
// ПРО «КОНКУРЕНТОВ». Признак is_competitor в данных равен ровно «страна = Россия» —
// расхождений ноль на всех 2 652 строках (на 28.07.2026), и мы сами тоже им помечены. Фильтр
// подписан «российские компании», а не «конкуренты»: выдавать признак за экспертную оценку
// нельзя. Наши собственные юрлица помечены отдельно («это мы») по списку из ядра.
//
// ПРО АДРЕС И ДАТУ НАЧАЛА. Адрес пуст во всех 2 652 строках (на 28.07.2026), дата начала — тоже.
// Есть город и срок окончания. Экран об этом говорит, а не рисует прочерки.
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useP55Refs, useСервисникРС, useСервисникиРС } from '../api/p55'
import type { КарточкаСервисника, СервисникГруппа } from '../api/p55'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtDays, fmtText, plural, pluralN } from '../lib/format'
import {
  НАША_ОБЛАСТЬ, ПУТИ, РАЗМЕР_СТРАНИЦЫ, СКОРО_ДНЕЙ, видСвидетельства, видСрока, классСрока,
  короткаяОбласть, осталосьДней, подписьСрока,
} from '../lib/p55'
import type { ФильтрыP55 } from '../lib/p55'
import { Охват, Пейджер, Свежесть } from './RsVessels'

export default function RsSuppliers() {
  const [sp] = useSearchParams()
  const company = sp.get('company') || ''
  return company ? <Карточка company={company} /> : <Список />
}

// ═══════════════════════════════ список ═══════════════════════════════

function Список() {
  const [q, setQ] = useState('')
  const [deep, setDeep] = useState(false)
  const [country, setCountry] = useState('')
  const [city, setCity] = useState('')
  const [scope, setScope] = useState('')
  const [valid, setValid] = useState('')
  const [sort, setSort] = useState('company')
  const [start, setStart] = useState(0)

  const refs = useP55Refs()
  const счёт = refs.data?.counts
  const порог = refs.data?.expiring_days

  const фильтры = useMemo<ФильтрыP55>(() => ({
    q: q.trim() || undefined,
    deep: deep || undefined,
    country: country || undefined,
    city: city || undefined,
    scope: scope || undefined,
    valid: valid || undefined,
    sort,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [q, deep, country, city, scope, valid, sort, start])

  const запрос = useСервисникиРС(фильтры)
  const данные = запрос.data
  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  // Наша область признания — единственный отбор, который отвечает на «кто нам конкурент по
  // существу»: сервисник по спасательным плотам нам не конкурент, сколько бы Регистр ни
  // помечал его российским.
  const нашаОбласть = (refs.data?.supplier_scope ?? [])
    .find((s) => короткаяОбласть(s.scope) === НАША_ОБЛАСТЬ)

  return (
    <>
      <div className="page-head">
        <h1>Признанные сервисники</h1>
        <span className="muted">
          {данные ? `найдено компаний: ${данные.total}` : ''}
          {счёт ? ` · в реестре: ${pluralN(счёт.suppliers, 'компания', 'компании', 'компаний')},`
            + ` ${pluralN(счёт.supplier_lines, 'строка', 'строки', 'строк')}` : ''}
        </span>
      </div>

      <Охват scope={данные?._scope} />

      <div className="notice">
        <b>Одна строка списка — компания, а не запись реестра.</b> В данных на компанию
        приходится до пятидесяти строк: по одной на каждую область работ, поручителя и срок.
        Что именно компания вправе делать — в её карточке.
        <div className="hint" style={{ marginTop: 6 }}>
          Признак «российская компания» в данных подменяет собой пометку «конкурент»: они
          совпадают строка в строку, и наши собственные юрлица тоже помечены как конкуренты.
          Чтобы найти настоящих конкурентов, отбирайте по области признания
          «{НАША_ОБЛАСТЬ}»{нашаОбласть ? ` — таких компаний ${нашаОбласть.companies}` : ''}.
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="rs-suppliers">
          <div className="f" style={{ flex: 2, minWidth: 250 }}>
            <label htmlFor="rsq">Компания или № свидетельства</label>
            <input id="rsq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder='например: Общество, ООО "Фрегат" или 24.03.01.00782.120' />
          </div>

          <div className="f" style={{ minWidth: 230 }}>
            <label htmlFor="rsdeep">Как искать</label>
            <select id="rsdeep" value={deep ? '1' : ''}
                    onChange={(e) => сСброса(setDeep)(e.target.value === '1')}>
              <option value="">с начала названия (быстро)</option>
              <option value="1">внутри строки, город и поручитель</option>
            </select>
          </div>

          <div className="f" style={{ minWidth: 320 }}>
            <label htmlFor="rsscope">Область признания — что вправе делать</label>
            <select id="rsscope" value={scope} onChange={(e) => сСброса(setScope)(e.target.value)}>
              <option value="">любая</option>
              {(refs.data?.supplier_scope ?? []).map((s) => (
                <option key={s.scope} value={s.scope} title={s.scope}>
                  {короткаяОбласть(s.scope)} — компаний {s.companies}
                </option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 170 }}>
            <label htmlFor="rscountry">Страна</label>
            <select id="rscountry" value={country}
                    onChange={(e) => сСброса(setCountry)(e.target.value)}>
              <option value="">любая</option>
              {(refs.data?.supplier_country ?? []).map((c) => (
                <option key={c.k} value={c.k}>{c.k || '(не указана)'} ({c.n})</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="rscity">Город</label>
            <select id="rscity" value={city} onChange={(e) => сСброса(setCity)(e.target.value)}>
              <option value="">любой</option>
              {(refs.data?.supplier_city ?? []).map((c) => (
                <option key={c.k} value={c.k}>{c.k || '(не указан)'} ({c.n})</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 170 }}>
            <label htmlFor="rsvalid">Срок признания</label>
            <select id="rsvalid" value={valid} onChange={(e) => сСброса(setValid)(e.target.value)}>
              <option value="">любой</option>
              <option value="valid">действует сегодня</option>
              <option value="expiring">истекает ({fmtDays(порог ?? СКОРО_ДНЕЙ)})</option>
              <option value="expired">истёк</option>
            </select>
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="rssort">Сортировка</label>
            <select id="rssort" value={sort} onChange={(e) => сСброса(setSort)(e.target.value)}>
              <option value="company">по названию</option>
              <option value="city">по городу</option>
              <option value="country">по стране</option>
              <option value="lines">по числу признаний</option>
              <option value="valid">по дальнему сроку</option>
            </select>
          </div>
        </FilterBar>

        {valid && (
          <div className="hint" style={{ marginTop: 10 }}>
            Фильтр по сроку отбирает <b>строки</b> признания, а компания попадает в список,
            если под условие подошла хотя бы одна её строка. У компании с двадцатью
            признаниями одно может быть просрочено, а девятнадцать — нет.
          </div>
        )}
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Компания</th>
                  <th className="nowrap">Город</th>
                  <th className="nowrap">Страна</th>
                  <th className="nowrap">Областей</th>
                  <th className="nowrap">Поручителей</th>
                  <th className="nowrap">Признание до</th>
                  <th className="nowrap">Строк истекло</th>
                </tr>
              </thead>
              <tbody>
                {(данные?.rows ?? []).map((s) => <Строка key={s.company} s={s} порог={порог} />)}
                {данные && данные.rows.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <div className="empty">
                        <div className="empty-title">Под эти условия компаний нет</div>
                        <div className="empty-hint">
                          {q.trim() && !deep
                            ? 'Ищем с начала названия. Регистр пишет полное юридическое имя'
                              + ' («Общество с ограниченной ответственностью "…"»), поэтому'
                              + ' «Фрегат» с начала не найдётся — переключите «как искать».'
                            /* Число берём из справочника, а не пишем словами: в реестре
                               оно меняется с каждым синком, а «281» осталось бы навсегда. */
                            : `В реестре ${счёт
                              ? pluralN(счёт.suppliers, 'компания', 'компании', 'компаний')
                              : 'меньше трёхсот компаний'}: возможно, эта организация`
                              + ' признана не Регистром, а другим классификационным обществом.'}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Пейджер start={данные?.start ?? 0} total={данные?.total ?? 0}
                   next={данные?.next ?? null} busy={запрос.isFetching} onStart={setStart} />
          <Свежесть refs={refs.data} />
        </div>
      </QueryState>
    </>
  )
}

function Строка({ s, порог }: { s: СервисникГруппа; порог: number | undefined }) {
  const дней = осталосьДней(s.valid_to_last)
  const срок = видСрока(дней, порог)
  return (
    <tr>
      <td data-label="Компания">
        <Link to={ПУТИ.сервисник(s.company)}>{s.company}</Link>
        {s.is_ours && <> <span className="badge won">это мы</span></>}
      </td>
      <td className="nowrap" data-label="Город">
        {s.city || <span className="muted">не указан</span>}
      </td>
      <td className="nowrap" data-label="Страна">{fmtText(s.country)}</td>
      <td className="num" data-label="Областей">{s.scopes}</td>
      <td className="num" data-label="Поручителей">{s.authorizers}</td>
      <td className="nowrap" data-label="Признание до">
        {fmtDate(s.valid_to_last)}
        <div><span className={`badge ${классСрока(срок)}`}>{подписьСрока(дней, порог)}</span></div>
      </td>
      <td className="num" data-label="Строк истекло">
        {s.lines_expired > 0
          ? <span className="badge lose">{s.lines_expired} из {s.lines}</span>
          : <span className="muted">—</span>}
      </td>
    </tr>
  )
}

// ═══════════════════════════════ карточка ═══════════════════════════════

function Карточка({ company }: { company: string }) {
  const запрос = useСервисникРС(company)
  const refs = useP55Refs()
  const s = запрос.data
  const порог = refs.data?.expiring_days

  return (
    <>
      <div className="page-head">
        <h1>{s?.company || company}</h1>
        <div className="spacer" />
        <Link to={ПУТИ.сервисники}>← к реестру сервисников</Link>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {s && (
          <>
            {s.is_ours && (
              <div className="notice">
                <b>Это наша компания.</b> В данных она помечена признаком «конкурент» — потому
                что этот признак в реестре означает всего лишь «страна = Россия».
              </div>
            )}

            <div className="grid-2">
              <div>
                <ЧтоВправеДелать s={s} порог={порог} />
                <ВсеСтроки s={s} порог={порог} />
              </div>
              <div>
                <Реквизиты s={s} порог={порог} />
                <Свидетельства s={s} порог={порог} />
                <НашаКарточка s={s} />
              </div>
            </div>
          </>
        )}
      </QueryState>
    </>
  )
}

function Реквизиты({ s, порог }: { s: КарточкаСервисника; порог: number | undefined }) {
  const дней = осталосьДней(s.valid_to_last)
  return (
    <div className="card">
      <h2>В реестре РС</h2>
      <dl className="dl">
        <dt>Город</dt>
        <dd>{s.city || <span className="muted">в данных не указан</span>}</dd>
        <dt>Страна</dt><dd>{fmtText(s.country)}</dd>
        <dt>Адрес</dt>
        <dd className="muted">в данных пуст у всех компаний реестра — есть только город</dd>
        <dt>Свидетельств</dt><dd className="num">{s.certificates.length}</dd>
        <dt>Строк признания</dt>
        <dd className="num">
          {s.lines}
          <div className="hint">
            строка = область работ × поручитель × срок, поэтому их так много
          </div>
        </dd>
        <dt>Дальний срок</dt>
        <dd>
          {fmtDate(s.valid_to_last)}{' '}
          <span className={`badge ${классСрока(видСрока(дней, порог))}`}>
            {подписьСрока(дней, порог)}
          </span>
        </dd>
        <dt>Ближний срок</dt><dd>{fmtDate(s.valid_to_first)}</dd>
        <dt>Начало признания</dt>
        <dd className="muted">в данных нет ни у одной строки реестра</dd>
        <dt>Российская компания</dt>
        <dd>
          {s.is_competitor ? 'да' : 'нет'}
          <div className="hint">
            в данных этим же признаком помечены «конкуренты» — это одно и то же поле
          </div>
        </dd>
      </dl>
    </div>
  )
}

function ЧтоВправеДелать({ s, порог }: { s: КарточкаСервисника; порог: number | undefined }) {
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Что вправе делать</h2>
        <div className="spacer" />
        <span className="muted">областей признания: {s.scopes.length}</span>
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Область признания</th>
              <th className="nowrap">До</th>
              <th className="nowrap">Строк</th>
              <th>По чьему поручению</th>
            </tr>
          </thead>
          <tbody>
            {s.scopes.map((о) => {
              const дней = осталосьДней(о.valid_to_last)
              return (
                <tr key={о.scope}>
                  <td data-label="Область признания">
                    <b>{короткаяОбласть(о.scope)}</b>
                    <div className="hint">{о.scope}</div>
                  </td>
                  <td className="nowrap" data-label="До">
                    {fmtDate(о.valid_to_last)}
                    <div>
                      <span className={`badge ${классСрока(видСрока(дней, порог))}`}>
                        {подписьСрока(дней, порог)}
                      </span>
                    </div>
                  </td>
                  <td className="num" data-label="Строк">
                    {о.lines}
                    {о.expired_lines > 0 && (
                      <div className="hint">истекло {о.expired_lines}</div>
                    )}
                  </td>
                  <td data-label="По чьему поручению">
                    {о.authorizers.length === 0
                      ? <span className="muted">поручитель не указан</span>
                      : (
                        <>
                          {о.authorizers.slice(0, 6).join(', ')}
                          {о.authorizers.length > 6 && (
                            <div className="hint">и ещё {о.authorizers.length - 6}</div>
                          )}
                        </>
                      )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="hint" style={{ marginTop: 8 }}>
        «По чьему поручению» — изготовитель оборудования, за которым Регистр признал компанию
        (FURUNO, JOTRON, VIKING). Полная формулировка области — как её печатает Регистр,
        сразу по-русски и по-английски; короткая подпись наша.
      </div>
      <div className="hint">
        {s.data_notes.no_vessel_link ?? 'Связи «сервисник ↔ судно» в реестре РС нет: по этим'
          + ' данным нельзя сказать, какие суда он обслуживает.'}
      </div>
    </div>
  )
}

function ВсеСтроки({ s, порог }: { s: КарточкаСервисника; порог: number | undefined }) {
  const [развёрнуто, развернуть] = useState(false)
  if (!развёрнуто) {
    return (
      <div className="card">
        <h2>Все строки реестра ({s.lines})</h2>
        <div className="hint" style={{ marginBottom: 8 }}>
          Свёрнуто: выше то же самое сгруппировано по области работ. Разворачивать нужно, когда
          сверяют конкретное свидетельство с бумагой.
        </div>
        <button onClick={() => развернуть(true)}>
          показать {plural(s.lines, 'всю', 'все', 'все')}{' '}
          {pluralN(s.lines, 'строку', 'строки', 'строк')}
        </button>
      </div>
    )
  }
  return (
    <div className="card">
      <div className="page-head" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>Все строки реестра</h2>
        <div className="spacer" />
        <button onClick={() => развернуть(false)}>свернуть</button>
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th className="nowrap">Свидетельство</th>
              <th className="nowrap">До</th>
              <th>Область</th>
              <th>Поручитель</th>
              <th className="nowrap">Реестр РС</th>
            </tr>
          </thead>
          <tbody>
            {s.rows.map((r) => (
              <tr key={r.src_id}>
                <td className="mono nowrap" data-label="Свидетельство">{fmtText(r.certificate_no)}</td>
                <td className="nowrap" data-label="До">
                  {fmtDate(r.valid_to)}
                  <div>
                    <span className={`badge ${классСрока(видСрока(r.days_left, порог))}`}>
                      {подписьСрока(r.days_left, порог)}
                    </span>
                  </div>
                </td>
                <td data-label="Область">{короткаяОбласть(r.service_scope)}</td>
                <td data-label="Поручитель">{fmtText(r.authorizer)}</td>
                <td className="nowrap" data-label="Реестр РС">
                  {r.doc_url
                    ? <a href={r.doc_url} target="_blank" rel="noreferrer">открыть ↗</a>
                    : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Сколько свидетельств показываем в карточке компании. Число решает ЭКРАН (ядро отдаёт их
 * все), поэтому оно и живёт здесь одной константой: раньше «20» стояло в трёх местах —
 * в срезе, в условии «обрезали ли» и словами в подписи, — и разъехаться им ничто не мешало.
 */
const СВИДЕТЕЛЬСТВ_В_КАРТОЧКЕ = 20

function Свидетельства({ s, порог }: { s: КарточкаСервисника; порог: number | undefined }) {
  return (
    <div className="card">
      <h2>Её свидетельства на изделия</h2>
      {s.products.length === 0 ? (
        <div className="empty">
          <div className="empty-title">
            Свидетельств на изделия и на предприятие у этой компании в реестре нет — только
            признание как сервисной организации.
          </div>
        </div>
      ) : (
        <>
          <ul className="plain-list">
            {s.products.slice(0, СВИДЕТЕЛЬСТВ_В_КАРТОЧКЕ).map((p) => {
              const вид = видСвидетельства(p.certificate_type)
              return (
                <li key={p.src_id}>
                  <Link to={ПУТИ.изделие(p.src_id)}>
                    {p.product_name || вид.title}
                  </Link>
                  <div className="hint">
                    <span className="badge" title={вид.title}>{вид.code}</span>{' '}
                    <span className="mono">{fmtText(p.certificate_no)}</span> · до{' '}
                    {fmtDate(p.valid_to)} ·{' '}
                    <span className={`badge ${классСрока(видСрока(p.days_left, порог))}`}>
                      {подписьСрока(p.days_left, порог)}
                    </span>
                  </div>
                </li>
              )
            })}
          </ul>
          {s.products.length > СВИДЕТЕЛЬСТВ_В_КАРТОЧКЕ && (
            <div className="hint" style={{ marginTop: 8 }}>
              Показано {СВИДЕТЕЛЬСТВ_В_КАРТОЧКЕ} из {s.products.length}. Остальные —{' '}
              <Link to={`${ПУТИ.изделия}?q=${encodeURIComponent(s.company)}`}>
                в реестре изделий по этому изготовителю
              </Link>.
            </div>
          )}
        </>
      )}
    </div>
  )
}

function НашаКарточка({ s }: { s: КарточкаСервисника }) {
  return (
    <div className="card">
      <h2>Она же у нас в CRM</h2>
      {s.our_company.length === 0 ? (
        <div className="empty">
          <div className="empty-title">Компании с точно таким названием у нас нет.</div>
          <div className="empty-hint">
            Сравнение только точное: Регистр пишет полное юридическое имя, а в карточке у нас
            обычно сокращённое — «похожее» сравнение подсунуло бы чужую компанию. Найдите её
            вручную в разделе «Компании».
          </div>
        </div>
      ) : (
        <ul className="plain-list">
          {s.our_company.map((c) => (
            <li key={c.bitrix_id}>
              <EntityLink kind="company" id={c.bitrix_id} title={c.title} />
              <div className="hint">
                {fmtText(c.company_type)}
                {c.industry ? ` · ${c.industry}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
